import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  CircularProgress,
  Chip,
  Alert,
  Typography,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

interface ClassGroup {
  id: string;
  name: string;
  isActive: boolean;
}

interface ClassModel {
  id: string;
  name: string;
  code?: string;
  sequenceOrder: number;
  isActive: boolean;
  classGroupId: string;
  classGroup?: ClassGroup;
}

const ClassMaster: React.FC = () => {
  const [classes, setClasses] = useState<ClassModel[]>([]);
  const [classGroups, setClassGroups] = useState<ClassGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    sequenceOrder: 0,
    classGroupId: '',
    isActive: true,
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClassGroups();
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      fetchClasses(selectedGroupId);
    } else {
      setClasses([]);
    }
  }, [selectedGroupId]);

  const fetchClassGroups = async () => {
    try {
      const res = await axios.get('/system-settings/class-group?isActive=true');
      setClassGroups(res.data);
    } catch (err) {
      console.error('Failed to fetch class groups', err);
    }
  };

  const fetchClasses = async (groupId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/system-settings/classes`, { params: { classGroupId: groupId } });
      setClasses(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch classes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({
      name: '',
      code: '',
      sequenceOrder: classes.length + 1,
      classGroupId: selectedGroupId || '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (cls: ClassModel) => {
    setEditingId(cls.id);
    setFormData({
      name: cls.name,
      code: cls.code || '',
      sequenceOrder: cls.sequenceOrder,
      classGroupId: cls.classGroupId,
      isActive: cls.isActive,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this class? If it is in use, it will be deactivated instead.')) return;
    try {
      const res = await axios.delete(`/system-settings/classes/${id}`);
      alert(res.data.message);
      if (selectedGroupId) fetchClasses(selectedGroupId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete class');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/classes/${id}/toggle-status`);
      setClasses((prev) =>
        prev.map((cls) =>
          cls.id === id ? { ...cls, isActive: !currentStatus } : cls
        )
      );
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to toggle status');
      if (selectedGroupId) fetchClasses(selectedGroupId);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        sequenceOrder: Number(formData.sequenceOrder),
      };

      if (editingId) {
        await axios.patch(`/system-settings/classes/${editingId}`, payload);
      } else {
        await axios.post('/system-settings/classes', payload);
      }
      setDialogOpen(false);
      if (selectedGroupId) fetchClasses(selectedGroupId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save class');
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Class Master</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
                select
                label="Filter by Class Group"
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                sx={{ minWidth: 200 }}
                size="small"
                required
            >
                <MenuItem value="">Select Class Group</MenuItem>
                {classGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                        {group.name}
                    </MenuItem>
                ))}
            </TextField>
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={handleCreate}
                disabled={!selectedGroupId}
            >
                Add Class
            </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sequence</TableCell>
              <TableCell>Class Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Class Group</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow>
                    <TableCell colSpan={6} align="center"><CircularProgress /></TableCell>
                </TableRow>
            ) : classes.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={6} align="center">
                        {selectedGroupId ? 'No classes found in this group.' : 'Please select a Class Group to view classes.'}
                    </TableCell>
                </TableRow>
            ) : (
                classes.map((cls) => (
                  <TableRow key={cls.id}>
                    <TableCell>{cls.sequenceOrder}</TableCell>
                    <TableCell>{cls.name}</TableCell>
                    <TableCell>{cls.code || '-'}</TableCell>
                    <TableCell>{cls.classGroup?.name}</TableCell>
                    <TableCell>
                      <Switch
                        checked={cls.isActive}
                        onChange={() => handleToggleStatus(cls.id, cls.isActive)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEdit(cls)} title="Edit"><EditIcon /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(cls.id)} title="Delete (if unused)"><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editingId ? 'Edit Class' : 'New Class'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 300 }}>
            <TextField
                select
                label="Class Group"
                value={formData.classGroupId}
                onChange={(e) => setFormData({ ...formData, classGroupId: e.target.value })}
                fullWidth
                required
            >
                {classGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                        {group.name}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                label="Class Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
            />
            <TextField
                label="Class Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                fullWidth
                placeholder="Optional"
            />
            <TextField
                label="Sequence Order"
                type="number"
                value={formData.sequenceOrder}
                onChange={(e) => setFormData({ ...formData, sequenceOrder: Number(e.target.value) })}
                fullWidth
                required
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                }
                label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ClassMaster;
